import { ArrowRightAltIcon, ClearBadge, Color } from "@bonliva-crm/ui";
import { useState } from "react";
import locale from "@bonliva-crm/locale";
import { Box, Chip, IconButton, makeStyles, Typography } from "@material-ui/core";
import { useFormField } from "@clientUtils";
import { SimpleProfessionFilterData } from "./utils";
import { ProfessionFilterModal } from "./components/profession-filter-modal";
import { FilterWithFavouriteProps } from "../../utils/filters-record-types";
import { NonCollapsibleFavFilter } from "../_base/non-collapsible-fav-filter";

export type SimpleProfessionFilterProps = FilterWithFavouriteProps;

export const SimpleProfessionFilter = (props: SimpleProfessionFilterProps) => {
  const [open, setOpen] = useState(false);
  const title = props.label ?? locale.Profession;
  const classes = useStyles();

  const {
    setFieldValue,
    field: { value },
    fieldState,
    formState,
    formContext: { resetField }
  } = useFormField<SimpleProfessionFilterData>(props.name);

  const removeProfession = (profession: string) => {
    const professions = value.professions.filter((p: string) => p !== profession);

    if (professions.length === 0) {
      resetField(props.name);
      return;
    }

    setFieldValue({ ...value, professions }, { shouldDirty: true });
  };

  return (
    <>
      {props.setFavorite ? (
        <NonCollapsibleFavFilter
          favorite={props.favorite}
          onSetFavorite={props.setFavorite}
          onTitleClick={() => setOpen(true)}
          title={title}
          isDirty={fieldState.isDirty}
          isClearable={fieldState.isDirty}
          onClear={(e) => {
            resetField(props.name);
            e.stopPropagation();
            e.preventDefault();
          }}
          action={
            <IconButton onClick={() => setOpen(true)}>
              <ArrowRightAltIcon />
            </IconButton>
          }
        >
          {value?.professions?.length > 0 && (
            <Box display="flex" flexWrap="wrap" flex="1" gridGap="8px" ml={1}>
              {value?.professions?.map((p) => (
                <Chip
                  size="small"
                  key={p}
                  label={p}
                  onDelete={() => removeProfession(p)}
                />
              ))}
              {value?.areaOfExpertises?.map((p) => {
                return <Chip size="small" key={p} label={p} />;
              })}
            </Box>
          )}
        </NonCollapsibleFavFilter>
      ) : (
        <ClearBadge
          invisible={!(value?.professions?.length > 0)}
          onClear={() => {
            resetField(props.name);
          }}
        >
          <div className={classes.favouriteButton} onClick={() => setOpen(true)}>
            <Typography variant="body1" style={{ color: Color.DetailsInput }}>
              {title}
            </Typography>

            <ArrowRightAltIcon fontSize="small" style={{ marginLeft: 8 }} />
            {value?.professions?.length > 0 && (
              <Box
                display="flex"
                flexWrap="wrap"
                flex="1"
                gridGap="8px"
                ml={1}
                alignItems={"center"}
              >
                {value?.professions?.map((p, i) => {
                  if (i === 4) {
                    return "...";
                  }
                  if (i > 4) {
                    return null;
                  }
                  return (
                    <Chip
                      size="small"
                      key={p}
                      label={p}
                      onDelete={() => removeProfession(p)}
                    />
                  );
                })}

                {value?.areaOfExpertises?.map((p, i) => {
                  if (i === 3) {
                    return "...";
                  }

                  if (i > 3) {
                    return null;
                  }

                  return <Chip size="small" key={p} label={p} />;
                })}
              </Box>
            )}
          </div>
        </ClearBadge>
      )}

      <ProfessionFilterModal
        initialValues={value}
        open={open}
        setOpen={setOpen}
        onClear={() => resetField(props.name)}
        onSubmit={(v) => {
          setFieldValue(v, { shouldDirty: true });
        }}
      />
    </>
  );
};

const useStyles = makeStyles({
  favouriteButton: {
    border: `1px solid ${Color.LightGray}`,
    borderRadius: "4px",
    padding: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",

    "&:hover": {
      backgroundColor: Color.LightGray
    }
  }
});
